/**
 * RigCalibration - Main page component for rig calibration management
 */

import './RigCalibration.css';
import { Calibration, InferenceResult, RetortHistory, Rig, Well } from '@/models/models';
import {
  fetchRigById,
  fetchWellsByRigId,
  fetchCalibration,
  saveCalibration,
  addRetortHistory,
} from '@/api/calibrationApi';

// Child components
import RetortHistoryComponent from './RetortHistory';
import TestResultsComponent from './TestResults';
import BucketSettingsCard from './BucketSettingsCard';
import CalibrationFactorsCard from './CalibrationFactorsCard';
import { ContentSkeleton } from '@/components/LoadingSkeleton/LoadingSkeleton';

// Constants, utils, types
import { DEFAULT_CALIBRATION_VALUES } from './constants';
import {
  clampCalibrationValue,
  getNumericValue,
  isUnauthorizedError,
  isForbiddenError,
  isNotFoundError,
  getApiErrorMessage,
} from './utils';
import { CalibrationValues, ValidationErrors } from './types';
import { validateCalibrationValues, validateBucket } from './validation';

// Mantine components
import { Alert, Box, Select, Text } from '@mantine/core';

import IconAlertCircle from '@tabler/icons-react/IconAlertCircle';

import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '@/i18n/LanguageContext';

export default function RigCalibration() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, language } = useLanguage();

  // Data state
  const [data, setData] = useState<Calibration | null>(null);
  const [rigData, setRigData] = useState<Rig | null>(null);
  const [wells, setWells] = useState<Well[]>([]);
  const [selectedWellId, setSelectedWellId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Calibration values state
  const [calibrationValues, setCalibrationValues] = useState<CalibrationValues>(DEFAULT_CALIBRATION_VALUES);
  const [originalCalibrationValues, setOriginalCalibrationValues] = useState<CalibrationValues>(DEFAULT_CALIBRATION_VALUES);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Retort wizard input fields
  const [density, setDensity] = useState<number | ''>('');
  const [oilPercent, setOilPercent] = useState<number | ''>('');
  const [waterPercent, setWaterPercent] = useState<number | ''>('');

  // Selection state
  const [selectedRetortHistory, setSelectedRetortHistory] = useState<RetortHistory | null>(null);
  const [selectedTestResult, setSelectedTestResult] = useState<InferenceResult | null>(null);

  const calibrationFactorsRef = useRef<HTMLDivElement>(null);

  const handleApiError = useCallback(
    (apiError: unknown, defaultMessage: string) => {
      const apiMessage = getApiErrorMessage(apiError, '').toLowerCase();

      if (isNotFoundError(apiError) && apiMessage.includes('rig not found')) {
        setError(t('calibration.rigNotFound'));
      } else if (isForbiddenError(apiError)) {
        setError(t('calibration.accessDenied'));
      } else if (isUnauthorizedError(apiError)) {
        setError(t('calibration.unauthorized'));
        setTimeout(() => navigate('/authorization'), 2000);
      } else {
        setError(getApiErrorMessage(apiError, defaultMessage));
      }
    },
    [navigate, t]
  );

  // Fetch rig calibration data on mount
  useEffect(() => {
    const loadCalibration = async () => {
      if (!id || isNaN(Number(id))) {
        setError(t('calibration.invalidRigId'));
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const rigId = Number(id);
        const requestedWellId = Number(searchParams.get('wellId'));

        const [rig, rigWells] = await Promise.all([
          fetchRigById(rigId),
          fetchWellsByRigId(rigId),
        ]);

        if (!rig) {
          setError(t('calibration.rigNotFound'));
          return;
        }

        setRigData(rig);
        setWells(rigWells);
        setSelectedWellId((prev) => {
          if (prev && rigWells.some((well) => well.id === prev)) {
            return prev;
          }
          if (
            Number.isFinite(requestedWellId) &&
            rigWells.some((well) => well.id === requestedWellId)
          ) {
            return requestedWellId;
          }
          return rigWells[0]?.id ?? null;
        });

        try {
          const calibrationData = await fetchCalibration(rigId);
          setData(calibrationData);
          const loadedValues = {
            active_oil_calibration: calibrationData.active_oil_calibration,
            active_water_calibration: calibrationData.active_water_calibration,
            ls_effluent_oil_calibration: calibrationData.ls_effluent_oil_calibration,
            ls_effluent_water_calibration: calibrationData.ls_effluent_water_calibration,
            ls_return_oil_calibration: calibrationData.ls_return_oil_calibration,
            ls_return_water_calibration: calibrationData.ls_return_water_calibration,
            hs_effluent_oil_calibration: calibrationData.hs_effluent_oil_calibration,
            hs_effluent_water_calibration: calibrationData.hs_effluent_water_calibration,
            hs_discard_oil_calibration: calibrationData.hs_discard_oil_calibration,
            hs_discard_water_calibration: calibrationData.hs_discard_water_calibration,
            drying_shaker_discard_oil_calibration: calibrationData.drying_shaker_discard_oil_calibration,
            drying_shaker_discard_water_calibration: calibrationData.drying_shaker_discard_water_calibration,
            drying_effluent_oil_calibration: calibrationData.drying_effluent_oil_calibration,
            drying_effluent_water_calibration: calibrationData.drying_effluent_water_calibration,
            new_mud_oil_calibration: calibrationData.new_mud_oil_calibration,
            new_mud_water_calibration: calibrationData.new_mud_water_calibration,
            random_oil_calibration: calibrationData.random_oil_calibration,
            random_water_calibration: calibrationData.random_water_calibration,
          };
          setCalibrationValues(loadedValues);
          setOriginalCalibrationValues(loadedValues);
        } catch (calibrationError: unknown) {
          // If calibration not found (404), initialize with defaults
          if (isNotFoundError(calibrationError)) {
            const defaultCalibration: Calibration = {
              rig_id: rigId,
              ...DEFAULT_CALIBRATION_VALUES,
              created_at: new Date().toISOString(),
              updated_at: null,
            };
            setData(defaultCalibration);
            setCalibrationValues(DEFAULT_CALIBRATION_VALUES);
            setOriginalCalibrationValues(DEFAULT_CALIBRATION_VALUES);
          } else {
            throw calibrationError;
          }
        }
      } catch (error: unknown) {
        console.error('Error fetching calibration:', error);
        handleApiError(error, t('calibration.loadError'));
      } finally {
        setLoading(false);
      }
    };

    loadCalibration();
  }, [handleApiError, id, searchParams, t]);

  // Handle calibration value change with validation
  const handleCalibrationValueChange = useCallback((field: keyof CalibrationValues, value: number) => {
    setCalibrationValues((prev) => {
      const newValues = { ...prev, [field]: value };

      // Determine bucket prefix from field name
      const prefix = field.replace('_oil_calibration', '').replace('_water_calibration', '');
      const oilField = `${prefix}_oil_calibration` as keyof CalibrationValues;
      const waterField = `${prefix}_water_calibration` as keyof CalibrationValues;

      // Validate the bucket
      const errors = validateBucket(
        newValues[oilField],
        newValues[waterField],
        prefix,
        language
      );

      setValidationErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        // Remove old errors for this bucket
        delete newErrors[oilField];
        delete newErrors[waterField];
        delete newErrors[`${prefix}_sum`];
        // Add new errors
        Object.assign(newErrors, errors);
        return newErrors;
      });

      return newValues;
    });
  }, [language]);

  // Validate all calibration values
  const validateAllCalibrationValues = useCallback((): boolean => {
    const errors = validateCalibrationValues(calibrationValues);
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [calibrationValues]);

  // Save calibration to server
  const handleSaveCalibration = useCallback(async () => {
    if (!id || isNaN(Number(id))) return;
    if (!validateAllCalibrationValues()) return;

    try {
      setSaving(true);
      setSaveSuccess(false);
      setError(null);

      await saveCalibration(Number(id), calibrationValues);

      // Update local data
      const updatedData: Calibration = data
        ? { ...data, ...calibrationValues }
        : {
            rig_id: Number(id),
            ...calibrationValues,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          };
      setData(updatedData);
      setOriginalCalibrationValues(calibrationValues);

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error: unknown) {
      console.error('Error saving calibration:', error);
      handleApiError(error, t('calibration.saveError'));
    } finally {
      setSaving(false);
    }
  }, [id, data, calibrationValues, validateAllCalibrationValues, handleApiError, t]);

  // Apply calibration factors from selected test result and retort data
  const handleApplyCalibrationFactors = useCallback(async () => {
    if (!selectedTestResult || !selectedRetortHistory || !id || isNaN(Number(id))) return;

    const slot = selectedRetortHistory.sample_source;
    if (!slot) return;

    // Get retort values (from selected history or input fields)
    const predictedOil = selectedTestResult.base_oil_vol_per_predicted_before_calibration ?? 0;
    const predictedWater = selectedTestResult.water_vol_per_predicted_before_calibration ?? 0;
    const retortOil = selectedRetortHistory
      ? selectedRetortHistory.oil_percentage
      : getNumericValue(oilPercent);
    const retortWater = selectedRetortHistory
      ? selectedRetortHistory.water_percentage
      : getNumericValue(waterPercent);

    // Calculate and clamp factors
    const oilFactor = clampCalibrationValue(predictedOil - retortOil);
    const waterFactor = clampCalibrationValue(predictedWater - retortWater);

    // Update calibration values based on sample source
    const newCalibrationValues = { ...calibrationValues };
    const oilField = `${slot}_oil_calibration` as keyof CalibrationValues;
    const waterField = `${slot}_water_calibration` as keyof CalibrationValues;

    newCalibrationValues[oilField] = oilFactor;
    newCalibrationValues[waterField] = waterFactor;

    setCalibrationValues(newCalibrationValues);

    // Validate new values
    const errors = validateCalibrationValues(newCalibrationValues);
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    // Save to server
    try {
      setSaving(true);
      setSaveSuccess(false);
      setError(null);

      await saveCalibration(Number(id), newCalibrationValues);

      // Update local data
      const updatedData: Calibration = data
        ? { ...data, ...newCalibrationValues }
        : {
            rig_id: Number(id),
            ...newCalibrationValues,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          };
      setData(updatedData);
      setOriginalCalibrationValues(newCalibrationValues);

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);

      // Scroll to Calibration factors and highlight the source
      calibrationFactorsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

      // Save to retort history if using input fields
      if (!selectedRetortHistory && selectedWellId && density !== '' && oilPercent !== '' && waterPercent !== '') {
        try {
          await addRetortHistory(
            selectedWellId,
            getNumericValue(density),
            getNumericValue(oilPercent),
            getNumericValue(waterPercent)
          );
        } catch (retortError: unknown) {
          console.error('Error saving retort history:', retortError);
        }
      }
    } catch (error: unknown) {
      console.error('Error saving calibration:', error);
      handleApiError(error, t('calibration.saveError'));
    } finally {
      setSaving(false);
    }
  }, [
    id,
    selectedTestResult,
    selectedRetortHistory,
    oilPercent,
    waterPercent,
    density,
    selectedWellId,
    calibrationValues,
    data,
    handleApiError,
    t,
  ]);

  // Loading state
  if (loading) {
    return <ContentSkeleton />;
  }

  // Error state
  if (error) {
    return (
      <Alert
        icon={<IconAlertCircle size="1rem" />}
        title={t('common.error')}
        color="red"
        withCloseButton
        onClose={() => navigate('/inference_results')}
      >
        {error}
      </Alert>
    );
  }

  return (
    <Box w="100%" className="rc-pageBox">
        <div className="rc-contextBar">
          <div className="rc-contextValue">
            <Text size="xs" c="dimmed" fw={700} tt="uppercase">
              {t('calibration.rig')}
            </Text>
            <Text size="md" fw={800}>{rigData?.name || '-'}</Text>
          </div>
          <Select
            label={t('calibration.well')}
            value={selectedWellId ? String(selectedWellId) : null}
            data={wells.map((well) => ({
              value: String(well.id),
              label: well.name,
            }))}
            onChange={(value) => {
              setSelectedWellId(value ? Number(value) : null);
              setSelectedRetortHistory(null);
            }}
            placeholder={t('calibration.selectWell')}
            className="rc-wellSelect"
            searchable
            limit={100}
            nothingFoundMessage={t('calibration.noWells')}
            comboboxProps={{ withinPortal: false }}
          />
        </div>

        <div className="rc-workflowGrid">
          <div className="rc-retortHistoryWrapper">
            <RetortHistoryComponent
              wellId={selectedWellId ?? 0}
              selectedRetortHistory={selectedRetortHistory}
              onRetortHistorySelect={setSelectedRetortHistory}
              density={density}
              oilPercent={oilPercent}
              waterPercent={waterPercent}
              onDensityChange={setDensity}
              onOilPercentChange={setOilPercent}
              onWaterPercentChange={setWaterPercent}
            />
          </div>

          <div className="rc-testResultsWrapper">
            <TestResultsComponent
              rigId={Number(id)}
              rigData={rigData}
              selectedTestResult={selectedTestResult}
              onTestResultSelect={setSelectedTestResult}
              selectedRetortHistory={selectedRetortHistory}
            />
          </div>

          <div className="rc-applyWrapper">
            <CalibrationFactorsCard
              selectedTestResult={selectedTestResult}
              selectedRetortHistory={selectedRetortHistory}
              density={density}
              oilPercent={oilPercent}
              waterPercent={waterPercent}
              saving={saving}
              onApply={handleApplyCalibrationFactors}
            />
          </div>

          <aside className="rc-bucketSettingsWrapper" ref={calibrationFactorsRef}>
            <Text size="xs" c="dimmed" className="rc-settingsDescription">
              {t('calibration.settingsDescription')}
            </Text>
            <BucketSettingsCard
              calibrationValues={calibrationValues}
              validationErrors={validationErrors}
              saving={saving}
              saveSuccess={saveSuccess}
              hasUnsavedChanges={JSON.stringify(calibrationValues) !== JSON.stringify(originalCalibrationValues)}
              onCalibrationValueChange={handleCalibrationValueChange}
              onSave={handleSaveCalibration}
              onSaveSuccessClose={() => setSaveSuccess(false)}
              onValidationErrorsClose={() => setValidationErrors({})}
            />
          </aside>
        </div>
    </Box>
  );
}
